import { useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  BackgroundVariant,
  Panel,
  ReactFlowProvider
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useFlowchartStore, FlowchartData } from '../../stores/flowchartStore';
import FlowchartNodeToolbar from './FlowchartNodeToolbar';
import FlowchartEdgeToolbar from './FlowchartEdgeToolbar';
import styles from './FlowchartEditor.module.css';
import '../../styles/reactflow-overrides.css';
import { RectangleNode, CircleNode, EllipseNode, DiamondNode, ParallelogramNode } from './CustomNodes';

// @ts-ignore - React Flow node types
const nodeTypes = {
  rectangle: RectangleNode,
  circle: CircleNode,
  ellipse: EllipseNode,
  diamond: DiamondNode,
  parallelogram: ParallelogramNode,
};

function FlowchartEditorContent() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { currentFlowchart, fetchFlowchart, saveFlowchartContent } = useFlowchartStore();
  
  const [nodes, setNodes, onNodesChange] = useNodesState<any>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<any>([]);
  const [selectedNode, setSelectedNode] = useState<any | null>(null);
  const [selectedEdge, setSelectedEdge] = useState<any | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Load flowchart data
  useEffect(() => {
    if (slug) {
      fetchFlowchart(slug);
    }
  }, [slug, fetchFlowchart]);

  // Initialize flowchart when data is loaded
  useEffect(() => {
    if (currentFlowchart?.content) {
      const { content } = currentFlowchart;
      
      // Transform nodes
      const loadedNodes = content.nodes.map((node: any) => ({
        id: node.id,
        type: node.type || 'default',
        position: node.position,
        data: {
          label: node.data?.label || 'Node',
          style: node.data?.style || node.style,
          shape: node.data?.shape || node.type,
          connections: node.data?.connections || 'all'
        },
        style: node.style
      }));
      
      // Transform edges
      const loadedEdges = content.edges.map((edge: any) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.label,
        style: {
          stroke: edge.style?.stroke || edge.data?.style?.stroke || '#9333ea',
          strokeWidth: edge.style?.strokeWidth || edge.data?.style?.strokeWidth || 2,
          strokeDasharray: edge.style?.strokeDasharray || edge.data?.style?.strokeDasharray || '0'
        }
      }));
      
      setNodes(loadedNodes);
      setEdges(loadedEdges);
      setHasUnsavedChanges(false);
    }
  }, [currentFlowchart, setNodes, setEdges]);

  // Handle node selection
  const onNodeClick = useCallback((_event: React.MouseEvent, node: any) => {
    setSelectedNode(node);
    setSelectedEdge(null);
  }, []);

  // Handle edge selection
  const onEdgeClick = useCallback((_event: React.MouseEvent, edge: any) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
  }, []);

  // Handle clicking on canvas (deselect)
  const onPaneClick = useCallback(() => {
    setSelectedNode(null);
    setSelectedEdge(null);
  }, []);

  // Handle creating new connections
  const onConnect = useCallback(
    (params: Connection) => {
      const newEdge: any = {
        ...params,
        id: `edge-${Date.now()}`,
        label: '',
        style: {
          stroke: '#9333ea',
          strokeWidth: 2,
          strokeDasharray: '0'
        }
      };
      setEdges((eds) => addEdge(newEdge, eds));
      setHasUnsavedChanges(true);
    },
    [setEdges]
  );

  // Handle node updates from toolbar
  const updateNode = useCallback((nodeId: string, updates: any) => {
    setNodes((nds: any) =>
      nds.map((node: any) => {
        if (node.id === nodeId) {
          const updated = {
            ...node,
            ...updates,
            data: {
              ...node.data,
              ...updates.data
            },
            style: {
              ...node.style,
              ...updates.style
            }
          };
          setSelectedNode(updated);
          return updated;
        }
        return node;
      })
    );
    setHasUnsavedChanges(true);
  }, [setNodes]);

  // Handle edge updates from toolbar
  const updateEdge = useCallback((edgeId: string, updates: any) => {
    setEdges((eds: any) =>
      eds.map((edge: any) => {
        if (edge.id === edgeId) {
          const updated = {
            ...edge,
            ...updates,
            style: {
              ...edge.style,
              ...updates.style
            }
          };
          setSelectedEdge(updated);
          return updated;
        }
        return edge;
      })
    );
    setHasUnsavedChanges(true);
  }, [setEdges]);

  // Delete selected node or edge
  const deleteSelected = useCallback(() => {
    if (selectedNode) {
      setNodes((nds: any) => nds.filter((node: any) => node.id !== selectedNode.id));
      setEdges((eds: any) => eds.filter((edge: any) => 
        edge.source !== selectedNode.id && edge.target !== selectedNode.id
      ));
      setSelectedNode(null);
      setHasUnsavedChanges(true);
    } else if (selectedEdge) {
      setEdges((eds: any) => eds.filter((edge: any) => edge.id !== selectedEdge.id));
      setSelectedEdge(null);
      setHasUnsavedChanges(true);
    }
  }, [selectedNode, selectedEdge, setNodes, setEdges]);

  // Add new node
  const addNode = useCallback((shape: string = 'rectangle') => {
    const shapeLabels: Record<string, string> = {
      rectangle: 'Process',
      circle: 'Start/End',
      ellipse: 'Process',
      diamond: 'Decision',
      parallelogram: 'Input/Output'
    };
    
    const newNode: any = {
      id: `node-${Date.now()}`,
      type: shape,
      position: { x: Math.random() * 400, y: Math.random() * 400 },
      data: { 
        label: shapeLabels[shape] || 'Node',
        style: {
          backgroundColor: '#1f2937',
          borderColor: '#9333ea',
          borderWidth: 2
        },
        shape: shape,
        connections: 'all'
      },
      // style: {
      //   backgroundColor: '#1f2937',
      //   borderColor: '#9333ea',
      //   borderWidth: 2
      // }
    };
    setNodes((nds) => [...nds, newNode]);
    setHasUnsavedChanges(true);
  }, [setNodes]);

  // Save flowchart
  const saveFlowchart = useCallback(async () => {
    if (!currentFlowchart) return;
    
    setIsSaving(true);
    try {
      const content: FlowchartData = {
        nodes: nodes.map((node: any) => ({
          id: node.id,
          type: node.type || 'default',
          position: node.position,
          data: {
            label: node.data?.label || 'Node',
            style: node.data?.style
          },
          style: node.style
        })),
        edges: edges.map((edge: any) => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          label: edge.label || '',
          style: edge.style
        })),
        viewport: { x: 0, y: 0, zoom: 1 }
      };
      
      await saveFlowchartContent(currentFlowchart.id, content);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving flowchart:', error);
    } finally {
      setIsSaving(false);
    }
  }, [currentFlowchart, nodes, edges, saveFlowchartContent]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl/Cmd + S to save
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        saveFlowchart();
      }
      
      // Delete or Backspace to delete selected
      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (selectedNode || selectedEdge) {
          event.preventDefault();
          deleteSelected();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [saveFlowchart, deleteSelected, selectedNode, selectedEdge]);

  return (
    <div style={{ width: '100vw', height: '100vh' }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeClick={onNodeClick}
        onEdgeClick={onEdgeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        fitView
        attributionPosition="bottom-left"
        deleteKeyCode={[]} // We handle deletion ourselves
      >
        <Background color="#e5e7eb" gap={16} variant={BackgroundVariant.Dots} />
        <Controls />
        
        {/* Top toolbar */}
        <Panel position="top-center" style={{ 
          backgroundColor: 'var(--bg-secondary)',
          padding: '8px 16px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          border: '1px solid var(--accent-alpha-15)',
          display: 'flex',
          gap: '8px',
          alignItems: 'center'
        }}>
          <button
            onClick={() => addNode('rectangle')}
            className={styles.editorButton}
            title="Add rectangle node"
          >
            ▭ Rectangle
          </button>
          <button
            onClick={() => addNode('circle')}
            className={styles.editorButton}
            title="Add circle node"
          >
            ○ Circle
          </button>
          <button
            onClick={() => addNode('diamond')}
            className={styles.editorButton}
            title="Add diamond node"
          >
            ◇ Diamond
          </button>
          <button
            onClick={() => addNode('ellipse')}
            className={styles.editorButton}
            title="Add ellipse node"
          >
            ⬭ Ellipse
          </button>
          <button
            onClick={() => addNode('parallelogram')}
            className={styles.editorButton}
            title="Add parallelogram node"
          >
            ▱ Parallelogram
          </button>
          <div className={styles.editorDivider} />
          <button
            onClick={deleteSelected}
            disabled={!selectedNode && !selectedEdge}
            className={styles.editorButton}
            title="Delete selected (Del)"
          >
            Delete
          </button>
          <button
            onClick={saveFlowchart}
            disabled={isSaving}
            className={styles.editorButtonPrimary}
            title="Save (Ctrl+S)"
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={() => navigate('/flowcharts')}
            className={styles.editorButton}
            title="Back to list"
          >
            ← Back
          </button>
        </Panel>

        {/* Node toolbar - floating */}
        {selectedNode && (
          <FlowchartNodeToolbar
            node={selectedNode}
            onUpdate={(updates) => updateNode(selectedNode.id, updates)}
            onClose={() => setSelectedNode(null)}
          />
        )}

        {/* Edge toolbar - floating */}
        {selectedEdge && (
          <FlowchartEdgeToolbar
            edge={selectedEdge}
            onUpdate={(updates) => updateEdge(selectedEdge.id, updates)}
            onClose={() => setSelectedEdge(null)}
          />
        )}

        {/* Status indicator */}
        <Panel position="bottom-right" style={{ 
          backgroundColor: 'var(--bg-secondary)',
          padding: '8px 12px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          border: '1px solid var(--accent-alpha-15)',
          fontSize: '12px',
          color: hasUnsavedChanges ? '#fbbf24' : 'var(--text-muted)'
        }}>
          {isSaving ? 'Saving...' : (hasUnsavedChanges ? '● Unsaved changes' : (lastSaved ? `✓ Saved: ${lastSaved.toLocaleTimeString()}` : '✓ Loaded'))}
        </Panel>
      </ReactFlow>
    </div>
  );
}

export default function FlowchartEditor() {
  return (
    <ReactFlowProvider>
      <FlowchartEditorContent />
    </ReactFlowProvider>
  );
}
